import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";

const credentials = [
  { icon: "ri-award-line", label: "Board Certified Endocrinologist", sub: "ABIM" },
  { icon: "ri-graduation-cap-line", label: "M.D., University of the West Indies", sub: "School of Medicine" },
  { icon: "ri-hospital-line", label: "Fellowship in Endocrinology", sub: "Howard University" },
  { icon: "ri-book-2-line", label: "Master of Divinity (M.Div.)", sub: "Gordon-Conwell Theological Seminary" },
  { icon: "ri-user-star-line", label: "Ordained Pastor", sub: "KCC" },
  { icon: "ri-microscope-line", label: "Clinical Research Publications", sub: "15+ peer-reviewed articles" },
];

const timeline = [
  { year: "1998", event: "Graduated Medical School, University Of W.I." },
  { year: "2001", event: "Completed Internal Medicine Residency" },
  { year: "2003", event: "Endocrinology Fellowship, Mayo Clinic" },
  { year: "2005", event: "Opened private endocrinology practice" },
  { year: "2010", event: "Ordained as Pastor, Grace Community Church" },
  { year: "2014", event: "Earned Master of Divinity degree" },
  { year: "2018", event: "Launched Faith & Wellness Ministry" },
  { year: "2022", event: "Expanded to telehealth consultations nationwide" },
];

export default function AboutPage() {
  return (
    <main>
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-stone-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex flex-col lg:flex-row items-center gap-16">
          {/* Image */}
          <div className="flex-shrink-0 w-full lg:w-96">
            <div className="relative">
              <div className="w-full h-[480px] rounded-2xl overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/634ceb34575d818adebfcccf7716833d/7eb00604763ac44771a7037e998618ab.png"
                  alt="Dr. Sheldon Mckenzie"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Badge */}
              <div className="absolute -bottom-5 -right-5 bg-amber-700 text-white rounded-2xl px-6 py-4 text-center">
                <p className="text-2xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>15+</p>
                <p className="text-xs opacity-80">Years of Excellence</p>
              </div>
            </div>
          </div>

          {/* Text */}
          <div className="flex-1">
            <span className="text-xs tracking-widest uppercase text-amber-700 font-semibold">About</span>
            <h1
              className="text-4xl md:text-5xl font-bold text-stone-800 mt-3 mb-6 leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Dr. Sheldon Mckenzie&apos;s
              <br />
              <span className="text-amber-700">Journey</span>
            </h1>
            <p className="text-stone-600 leading-relaxed mb-4 text-base">
              Dr. Sheldon Mckenzie is a board-certified endocrinologist with over 15 years of clinical experience
              specializing in diabetes, thyroid disorders, and hormonal health. His medical journey began
              at the University of the West Indies (U.W.I.), Jamaica, followed by a prestigious fellowship at
              Harvard University.
            </p>
            <p className="text-stone-600 leading-relaxed mb-4 text-base">
              Beyond medicine, Dr. Sheldon Mckenzie is an ordained pastor who has served his congregation for over
              a decade. He is Pastor with Kingdom Celebration Centre ministry and has his own flagship programme
              which airs on the Zoom platform every Tuesday at 8PM Eastern. He leads the Faith &amp; Wellness Ministry — a unique outreach
              that integrates evidence-based medicine with Christian spiritual care.
            </p>
            <p className="text-stone-600 leading-relaxed mb-8 text-base">
              His mission is simple: to treat the whole person — body, mind, and spirit — because true
              healing cannot be separated from faith.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/consultation"
                className="px-7 py-3 rounded-full bg-amber-700 text-white text-sm font-semibold hover:bg-amber-800 transition-all whitespace-nowrap cursor-pointer text-center"
              >
                Book a Consultation
              </Link>
              <Link
                to="/ministry"
                className="px-7 py-3 rounded-full border border-stone-300 text-stone-700 text-sm font-semibold hover:bg-stone-100 transition-all whitespace-nowrap cursor-pointer text-center"
              >
                Explore Ministry
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Credentials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="text-xs tracking-widest uppercase text-amber-700 font-semibold">Qualifications</span>
            <h2
              className="text-3xl md:text-4xl font-bold text-stone-800 mt-3"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Credentials &amp; Training
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {credentials.map((c) => (
              <div
                key={c.label}
                className="flex items-start gap-4 p-6 rounded-xl border border-stone-100 bg-stone-50 hover:border-amber-200 transition-all"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-amber-100 text-amber-700 flex-shrink-0 text-lg">
                  <i className={c.icon} />
                </div>
                <div>
                  <p className="font-semibold text-stone-800 text-sm">{c.label}</p>
                  <p className="text-xs text-stone-400 mt-0.5">{c.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="text-xs tracking-widest uppercase text-amber-700 font-semibold">History</span>
            <h2
              className="text-3xl md:text-4xl font-bold text-stone-800 mt-3"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              A Life of Purpose
            </h2>
          </div>
          <div className="relative">
            <div className="absolute left-6 top-0 bottom-0 w-px bg-amber-200" />
            <div className="flex flex-col gap-8">
              {timeline.map((item, i) => (
                <div key={i} className="flex items-start gap-6 pl-14 relative">
                  <div className="absolute left-3.5 top-1 w-5 h-5 flex items-center justify-center rounded-full bg-amber-700 border-4 border-amber-100 flex-shrink-0" />
                  <div>
                    <span className="text-xs font-bold text-amber-700 tracking-widest">{item.year}</span>
                    <p className="text-stone-700 text-sm mt-0.5">{item.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 bg-amber-700">
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
          <i className="ri-double-quotes-l text-amber-400 text-4xl mb-4 block" />
          <p
            className="text-2xl md:text-3xl text-white font-medium leading-relaxed mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            My calling is to bring healing in every sense of the word — to restore
            health to the body and hope to the soul, through the grace of God and
            the gift of medicine.
          </p>
          <p className="text-amber-200 text-sm">— Dr. Sheldon Mckenzie</p>
        </div>
      </section>

      <Footer />
    </main>
  );
}
